'use client';

import { useMemo, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { CONTACT } from '@/lib/contact';
import { FAQ_CATEGORIES, type FAQItem } from '@/components/common/faqSearch';

const OTHER_GROUP = 'More questions';

export default function FaqAccordionInteractive({ items }: { items: FAQItem[] }) {
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);

  const keyOf = (faq: FAQItem, i: number) => (faq.id != null ? `id-${faq.id}` : `q-${i}`);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    );
  }, [items, query]);

  // Group by buyer journey (audit T7). Categories render in FAQ_CATEGORIES
  // order; anything uncategorised  - e.g. rows added in the CMS before the
  // category is set  - collects in a trailing group rather than disappearing.
  const groups = useMemo(() => {
    const byCategory = new Map<string, { faq: FAQItem; index: number }[]>();
    filtered.forEach((faq, index) => {
      const name =
        faq.category && (FAQ_CATEGORIES as readonly string[]).includes(faq.category)
          ? faq.category
          : OTHER_GROUP;
      const bucket = byCategory.get(name);
      if (bucket) bucket.push({ faq, index });
      else byCategory.set(name, [{ faq, index }]);
    });
    return [...FAQ_CATEGORIES, OTHER_GROUP]
      .map((name) => ({ name, entries: byCategory.get(name) ?? [] }))
      .filter((group) => group.entries.length > 0);
  }, [filtered]);

  return (
    <div>
      {/* Search */}
      <div className="relative mb-8">
        <Icon
          name="MagnifyingGlassIcon"
          size={18}
          aria-hidden="true"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search questions..."
          aria-label="Search frequently asked questions"
          className="w-full min-h-11 pl-11 pr-4 py-3 bg-surface border border-border rounded-xl font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      <p className="sr-only" aria-live="polite">
        {filtered.length} question{filtered.length === 1 ? '' : 's'} shown
      </p>

      {filtered.length === 0 ? (
        <p className="py-12 text-center font-body text-muted-foreground">
          No questions match “{query}”. Try a different term.
        </p>
      ) : (
        <div className="space-y-12">
          {groups.map((group) => (
            <section key={group.name} aria-labelledby={`faq-group-${group.name}`}>
              <h2
                id={`faq-group-${group.name}`}
                className="mb-4 font-cta text-sm font-semibold uppercase tracking-wider text-primary"
              >
                {group.name}
              </h2>
              <div className="space-y-4">
                {group.entries.map(({ faq, index }) => {
                  const key = keyOf(faq, index);
                  const isOpen = openKey === key;
                  return (
                    <div
                      key={key}
                      className="bg-surface border border-border rounded-2xl overflow-hidden transition duration-300"
                    >
                      <h3 className="m-0">
                        <button
                          id={`faq-trigger-${key}`}
                          aria-expanded={isOpen}
                          aria-controls={`faq-panel-${key}`}
                          onClick={() => setOpenKey(isOpen ? null : key)}
                          className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-muted/30 transition active:scale-[0.99] duration-300"
                        >
                          <span className="font-cta text-lg font-semibold text-foreground">
                            {faq.question}
                          </span>
                          <Icon
                            name="ChevronDownIcon"
                            size={24}
                            aria-hidden="true"
                            className={`text-primary shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                      </h3>
                      <div
                        id={`faq-panel-${key}`}
                        role="region"
                        aria-labelledby={`faq-trigger-${key}`}
                        className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div className="px-6 pb-6">
                            <p className="font-body text-muted-foreground leading-relaxed whitespace-pre-line">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="mt-12 text-center">
        <p className="font-body text-muted-foreground mb-4">
          Still have questions? We&apos;re here to help.
        </p>
        <a
          href={`mailto:${CONTACT.email}`}
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-cta font-semibold transition-colors duration-300"
        >
          <Icon name="EnvelopeIcon" size={20} />
          Email us directly
        </a>
      </div>
    </div>
  );
}
